// Web Audio API playback engine.
//
// iOS Safari gates whether a *specific* <audio>/<video> element is allowed to
// play() without an active user gesture — unlocking one element does not
// unlock the next one, even seconds later. A Web Audio AudioContext works
// differently: once it has been resume()'d inside a real user gesture, the
// context stays "running" for the rest of the page's life, and *every*
// buffer played through it afterwards (including ones started from async
// code with no gesture of its own, e.g. after an API call resolves) is
// audible with no further interaction. This module plays every sound
// through one shared context so a single early unlock (see unlockAudioEngine)
// covers autoplay for the whole session.

let audioContext = null;
const bufferCache = new Map();

export function getAudioContext() {
	if (audioContext) {
		return audioContext;
	}

	const AudioContextCtor =
		(typeof window !== 'undefined' &&
			(window.AudioContext || window.webkitAudioContext)) ||
		null;

	if (!AudioContextCtor) {
		return null;
	}

	try {
		audioContext = new AudioContextCtor();
	} catch (_) {
		audioContext = null;
	}

	return audioContext;
}

function decodeAudioData(ctx, arrayBuffer) {
	return new Promise((resolve, reject) => {
		let settled = false;
		const onResolve = (buffer) => {
			if (settled) return;
			settled = true;
			resolve(buffer);
		};
		const onReject = (err) => {
			if (settled) return;
			settled = true;
			reject(err);
		};

		try {
			// Safari (incl. iOS 12) supports the older callback signature;
			// newer engines also return a Promise from the same call.
			const maybePromise = ctx.decodeAudioData(
				arrayBuffer,
				onResolve,
				onReject
			);
			if (maybePromise && typeof maybePromise.then === 'function') {
				maybePromise.then(onResolve, onReject);
			}
		} catch (err) {
			onReject(err);
		}
	});
}

export function loadBuffer(src) {
	if (!src) {
		return Promise.reject(new Error('No audio src provided'));
	}

	const ctx = getAudioContext();
	if (!ctx) {
		return Promise.reject(new Error('Web Audio API unavailable'));
	}

	if (bufferCache.has(src)) {
		return bufferCache.get(src);
	}

	const promise = fetch(src)
		.then((response) => response.arrayBuffer())
		.then((arrayBuffer) => decodeAudioData(ctx, arrayBuffer));

	bufferCache.set(src, promise);

	promise.catch(() => {
		// Don't poison the cache with a failed load; allow a later retry.
		bufferCache.delete(src);
	});

	return promise;
}

function resumeWithTimeout(ctx, timeoutMs) {
	if (ctx.state === 'running') {
		return Promise.resolve();
	}

	if (typeof ctx.resume !== 'function') {
		return Promise.resolve();
	}

	return new Promise((resolve, reject) => {
		let settled = false;

		const finish = () => {
			if (settled) return;
			settled = true;
			if (ctx.state === 'running') {
				resolve();
			} else {
				reject(new DOMException('Autoplay blocked', 'NotAllowedError'));
			}
		};

		try {
			ctx.resume().then(finish, finish);
		} catch (_) {
			finish();
		}

		// resume() can hang indefinitely on some WebKit builds when called
		// outside of a user gesture instead of rejecting — bound the wait so
		// callers can fall back (e.g. AudioPlayer's tap-to-retry listener).
		setTimeout(finish, timeoutMs);
	});
}

// Call this from inside a real, early user gesture (e.g. the very first tap
// of a session) to unlock the shared context for the rest of the page's life.
export function unlockAudioEngine() {
	const ctx = getAudioContext();
	if (!ctx) {
		return;
	}

	try {
		ctx.resume().catch(() => {});
	} catch (_) {}

	// A silent buffer play nudges some WebKit versions to fully commit the
	// unlock immediately rather than only on the next real sound.
	try {
		const source = ctx.createBufferSource();
		source.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
		source.connect(ctx.destination);
		source.start(0);
	} catch (_) {}
}

// A minimal, event-target-like wrapper around a Web Audio buffer source so it
// can be used as a near drop-in replacement for an HTMLMediaElement by
// AudioPlayer/playPause — exposing play()/pause()/currentTime/duration/
// paused/ended plus addEventListener/removeEventListener.
export class WebAudioVoice {
	constructor(src) {
		this.src = src;
		this._listeners = {};
		this._buffer = null;
		this._loadPromise = null;
		this._sourceNode = null;
		this._offset = 0;
		this._startedAtContextTime = 0;
		this._duration = 0;
		this._paused = true;
		this._ended = false;
	}

	addEventListener(type, handler) {
		if (!this._listeners[type]) {
			this._listeners[type] = [];
		}
		this._listeners[type].push(handler);
	}

	removeEventListener(type, handler) {
		if (!this._listeners[type]) {
			return;
		}
		this._listeners[type] = this._listeners[type].filter(
			(existing) => existing !== handler
		);
	}

	_emit(type) {
		const handlers = this._listeners[type];
		if (!handlers) {
			return;
		}
		handlers.slice().forEach((handler) => {
			try {
				handler({type, target: this});
			} catch (_) {}
		});
	}

	load() {
		if (this._loadPromise) {
			return this._loadPromise;
		}

		this._loadPromise = loadBuffer(this.src).then((buffer) => {
			this._buffer = buffer;
			this._duration = buffer.duration;
			this._emit('durationchange');
			this._emit('loadedmetadata');
			this._emit('loadeddata');
			this._emit('canplay');
			return buffer;
		});

		return this._loadPromise;
	}

	get duration() {
		return this._duration;
	}

	get paused() {
		return this._paused;
	}

	get ended() {
		return this._ended;
	}

	get currentTime() {
		if (this._paused) {
			return this._offset;
		}

		const ctx = getAudioContext();
		if (!ctx) {
			return this._offset;
		}

		return Math.min(
			this._offset + (ctx.currentTime - this._startedAtContextTime),
			this._duration || Infinity
		);
	}

	set currentTime(value) {
		const wasPlaying = !this._paused;
		this._stopSourceNode();
		this._offset = Math.max(0, value);
		this._ended = false;
		if (wasPlaying) {
			this._startSourceNode();
		}
	}

	play() {
		this._ended = false;

		return this.load().then(() => {
			const ctx = getAudioContext();
			if (!ctx) {
				throw new DOMException('Web Audio API unavailable', 'NotSupportedError');
			}

			return resumeWithTimeout(ctx, 300).then(() => {
				if (this._paused) {
					this._startSourceNode();
					this._paused = false;
					this._emit('play');
				}
			});
		});
	}

	pause() {
		if (this._paused) {
			return;
		}

		this._offset = this.currentTime;
		this._stopSourceNode();
		this._paused = true;
		this._emit('pause');
	}

	_startSourceNode() {
		const ctx = getAudioContext();
		if (!ctx || !this._buffer) {
			return;
		}

		const source = ctx.createBufferSource();
		source.buffer = this._buffer;
		source.connect(ctx.destination);
		source.onended = () => {
			if (this._sourceNode !== source) {
				// Stopped/replaced before naturally finishing — ignore.
				return;
			}
			this._sourceNode = null;
			this._paused = true;
			this._ended = true;
			this._offset = this._duration;
			this._emit('ended');
		};

		source.start(0, Math.min(this._offset, this._duration || 0));
		this._sourceNode = source;
		this._startedAtContextTime = ctx.currentTime;
	}

	_stopSourceNode() {
		if (!this._sourceNode) {
			return;
		}

		const node = this._sourceNode;
		node.onended = null;
		try {
			node.stop();
		} catch (_) {}
		try {
			node.disconnect();
		} catch (_) {}
		this._sourceNode = null;
	}

	destroy() {
		this._stopSourceNode();
		this._listeners = {};
	}
}
